import 'bootstrap/dist/css/bootstrap.min.css';
import HeaderComponent from '../Components/Header/Header';
import { useEffect, useState } from 'react';
import CalculateIcon from '@mui/icons-material/Calculate';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import QuoteTool from '../Components/QuoteTool/QuoteTool';
import HorizontalNonLinearStepper from '../Components/Stepper/Stepper';
import PartsCardView from '../Components/PartCardView/PartsCardView';
import AdminPartsListView from '../Components/AdminPartsListView/AdminPartsListView';
import InputRequirement from '../Components/InputRequirement/InputRequirement';
import ViewResults from '../Components/viewResults/ViewResults';
import GenerateQuotePage from '../Components/GenerateQuote/GenerateQuote';
import '../assets/css/Dashboard.scss';
import '../assets/css/common.scss';
import { getAllParts, downloadPart } from '../services/part/partService';
import noResultFoundImg from '../assets/images/no-result-found.svg';
import { getUser } from '../services/user/userService';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../redux/store/store';
import { showLoader } from '../redux/features/loaderSlice';
import { loginData } from '../redux/features/loginSlice';
import { showErrorToast } from '../utils/toast/notificationPopup';

export function HomePage() { 
  const [activeView, setActiveView] = useState<'dashboard' | 'quoteTool' | 'uploadDesign' | 'inputRequirement' | 'viewResults' | 'generateQuote'>('dashboard');
  const [partsList, setPartsList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPart, setSelectedPart] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('All Users');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<'PartName' | 'Username' | 'ModelType' | 'CreatedAt' | 'UpdatedAt' | 'Status'>('CreatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  const dispatch = useDispatch<AppDispatch>();
  const userData = useSelector((state: RootState) => state.login);
  const { userName } = userData;

  useEffect(() => {
    localStorage.setItem("amplify-authenticator-authState", "signedIn");
    fetchPartList();
    return () => {
      // Cleanup - removing current user session.
      localStorage.removeItem("amplify-authenticator-authState");
    };
  }, []);

  const fetchPartList = async () => {
    try {
      setIsLoading(true);
      dispatch(showLoader({ loaderVisibleState: true }));
      const userEmail = { EmailID: userName };
      const userInfo = await getUser(userEmail);

      if (!userInfo || userInfo.length === 0) {
        dispatch(showLoader({ loaderVisibleState: false }));
        setIsLoading(false);
        showErrorToast("Please contact your administrator for access");
        return;
      }

      const userIdObj = { UserID: userInfo[0].UserID };
      
      // Check if user is admin based on email domain
      const isMantle3DUser = userName.toLowerCase().endsWith('@mantle3d.com');
      setIsAdmin(isMantle3DUser);
      
      dispatch(
        loginData({ userName, userId: userInfo[0].UserID, isSignedIn: true })
      );
      const data = await getAllParts(userIdObj);

      if (data.length) {
        const sorted = data.sort((a, b) => {
          if (a.UpdatedAt && b.UpdatedAt) {
            return b.UpdatedAt < a.UpdatedAt ? -1 : 1;
          }
          return -1;
        });
        setPartsList(sorted);
      } else {
        setPartsList([]);
      }
    } catch (error) {
      showErrorToast("Error fetching parts");
    } finally {
      dispatch(showLoader({ loaderVisibleState: false }));
      setIsLoading(false);
    }
  };

  const handleSelectPart = (partDetails: any, viewResultsFlag: any) => {
    console.log('Part selected:', partDetails, viewResultsFlag);
    setSelectedPart({ ...partDetails, isEdit: viewResultsFlag === 'edit' });
    // If viewResultsFlag is "results", navigate to ViewResults page
    // Otherwise, navigate to InputRequirement (Edit mode)
    if (viewResultsFlag === 'results') {
      setActiveView('viewResults');
    } else {
      setActiveView('inputRequirement');
    }
  };

  const handleDownload = (data: any) => {
    downloadPart(data.PartID, data.PartName + "." + data.ModelType);
  };

  const handleLogoClick = async () => {
    setActiveView('dashboard');
    setSelectedPart(null);
    await fetchPartList();
  };

  const handleBackToDashboard = () => {
    setActiveView('dashboard');
    setSelectedPart(null);
  };

  const handleEditFromResults = () => {
    // When Edit Input Requirements is clicked from ViewResults, navigate to InputRequirement
    setActiveView('inputRequirement');
    // Update selectedPart to include isEdit flag
    if (selectedPart) {
      setSelectedPart({ ...selectedPart, isEdit: true });
    }
  };

  const handleNavigateToGenerateQuote = () => {
    // Navigate from ViewResults to Generate Quote page
    setActiveView('generateQuote');
  };

  const handleOrder = (partDetails: any) => {
    // Navigate to Generate Quote page when Order button is clicked
    setSelectedPart(partDetails);
    setActiveView('generateQuote');
  };

  // Helper function to get status from Processed field
  const getStatus = (processed: any) => {
    if (!processed || Object.keys(processed).length === 0) {
      return 'Not Available';
    }
    if (processed.error_msg) {
      return 'Error';
    }
    if (Object.values(processed).includes('False')) {
      return 'Processing';
    }
    if (Object.values(processed).includes('Error')) {
      return 'Error';
    }
    return 'Available';
  };

  // Get unique usernames for filter
  const uniqueUsers = Array.from(new Set(
    partsList.map(part => part.Username || userName).filter(Boolean)
  ));

  // Filter and sort parts
  const filteredParts = partsList.filter(part => {
    const matchesSearch = 
      part.PartName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.Username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const partUsername = part.Username || userName;
    const matchesUser = selectedUser === 'All Users' || partUsername === selectedUser;
    
    const partStatus = getStatus(part.Processed);
    const matchesStatus = !selectedStatus || 
      (selectedStatus === 'Available' && partStatus === 'Available') ||
      (selectedStatus === 'Not Available' && partStatus !== 'Available');
    
    return matchesSearch && matchesUser && matchesStatus;
  });

  // Sort parts
  const sortedParts = [...filteredParts].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle date fields
    if (sortField === 'CreatedAt' || sortField === 'UpdatedAt') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    // Handle string fields
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue?.toLowerCase() || '';
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleFilter = () => {
    // Trigger re-render with current filters
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedUser('All Users');
    setSelectedStatus('');
  };

  // Get parts with comments for notifications (admin only)
  const partsWithComments = isAdmin 
    ? partsList.filter(part => {
        // Filter parts that have available results (could have comments)
        const status = getStatus(part.Processed);
        return status === 'Available';
      }).map(part => ({
        ...part,
        Username: part.Username || userName
      }))
    : [];

  const handleNotificationClick = (part: any) => {
    // Navigate to the results page for the selected part
    setSelectedPart(part);
    setActiveView('viewResults');
  };

  return (
    <>
      <HeaderComponent 
        onLogoClick={handleLogoClick}
        notifications={partsWithComments}
        onNotificationClick={handleNotificationClick}
      />
      
      <div className="dashboard-container">
        {/* Toggle Switch Buttons */}
        <div className="dashboard-toggle-top">
          <button 
            className={`toggle-btn-top ${activeView === 'quoteTool' ? 'active' : ''}`}
            onClick={() => setActiveView('quoteTool')}
          >
            <CalculateIcon sx={{ width: '20px', height: '20px', marginRight: '8px' }} />
            Quick Quote
          </button>
          <button 
            className={`toggle-btn-top ${activeView === 'uploadDesign' ? 'active' : ''}`}
            onClick={() => setActiveView('uploadDesign')}
          >
            <UploadFileIcon sx={{ width: '20px', height: '20px', marginRight: '8px' }} />
            Upload Design
          </button>
        </div>

        {/* Dashboard Header */}
        {activeView === 'dashboard' && (
          <>
            {/* <div className="dashboard-header">
              <h1>Dashboard</h1>
            </div> */}

            {/* Simplified Single Row Controls */}
            <div className="filters-section">
              <button 
                className="sort-order-btn"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title={sortOrder === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
              >
                <SwapVertIcon />
              </button>

              <button 
                className="view-toggle-btn"
                onClick={() => setViewMode(viewMode === 'card' ? 'list' : 'card')}
                title={viewMode === 'card' ? 'Switch to List View' : 'Switch to Card View'}
              >
                {viewMode === 'card' ? <ViewListIcon /> : <ViewModuleIcon />}
              </button>

              <div className="search-box">
                <SearchIcon className="search-icon" />
                <input
                  type="text"
                  placeholder={isAdmin ? "Search by username or part name" : "Search by part name"}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

             

              

              

              <span className="show-label">Show</span>
              <select 
                className="items-per-page"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </>
        )}

        {/* Main Content Area */}
        <div className="dashboard-content">
          {activeView === 'dashboard' && (
            <>
            <h6 className='pageHeader'>Dashboard</h6>
              {viewMode === 'list' ? (
                <AdminPartsListView
                  partsList={sortedParts}
                  onSelectPart={handleSelectPart}
                  onDownload={handleDownload}
                  onOrder={handleOrder}
                  itemsPerPage={itemsPerPage}
                  getStatus={getStatus}
                  loggedInUser={userName}
                  noResultFoundImg={noResultFoundImg}
                  onUploadDesign={() => setActiveView('uploadDesign')}
                  isAdmin={isAdmin}
                />
              ) : (
                <div className="parts-card-view">
                  
                  {sortedParts.length ? (
                    sortedParts.slice(0, itemsPerPage).map((item: any) => (
                      <div key={item.PartID}>
                        
                        <PartsCardView
                          displayPart={item}
                          onSelectPart={handleSelectPart}
                          handleDownload={() => handleDownload(item)}
                          onOrder={() => handleOrder(item)}
                          loggedInUser={userName}
                          isAdmin={isAdmin}
                        />
                      </div>
                    ))
                  ) : (
                    !isLoading && (
                      <div className="empty-state">
                        <img src={noResultFoundImg} alt="No results found" style={{ maxWidth: '300px', marginBottom: '20px' }} />
                        <p>No Data available.</p>
                        <p> Click here to <a href="#" onClick={(e) => { e.preventDefault(); setActiveView('uploadDesign'); }} style={{ color: '#FF6B35', textDecoration: 'underline', cursor: 'pointer' }}>Upload Design</a> </p>
                      </div>
                    )
                  )}
                </div>
              )}
            </>
          )}

          {activeView === 'quoteTool' && (
            <QuoteTool />
          )}

          {activeView === 'uploadDesign' && (            
            <HorizontalNonLinearStepper />
          )}

          {activeView === 'inputRequirement' && selectedPart && (
            <InputRequirement
              partName={selectedPart.PartName}
              modelName={selectedPart.PartName + "." + selectedPart.ModelType}
              partId={selectedPart.PartID}
              handleNext={handleBackToDashboard}
              isEdit={selectedPart.isEdit}
              partIcon={selectedPart.icon}
            />
          )}

          {activeView === 'viewResults' && selectedPart && (
            <ViewResults
              partName={selectedPart.PartName}
              modelName={selectedPart.PartName + "." + selectedPart.ModelType}
              partId={selectedPart.PartID}
              handleNext={handleNavigateToGenerateQuote}
              onEditInputRequirements={handleEditFromResults}
              isAdmin={isAdmin}
            />
          )}

          {activeView === 'generateQuote' && selectedPart && (
            <GenerateQuotePage
              partName={selectedPart.PartName}
              modelName={selectedPart.PartName + "." + selectedPart.ModelType}
              partId={selectedPart.PartID}
              handleNext={handleBackToDashboard}
            />
          )}
        </div>
      </div>
    </>
  );
};
